import { Activity, Layers, Database, Cpu, Boxes, Network } from "lucide-react";
import { env } from "../lib/env";

// Numeric reasoning thresholds live in the server-side Pydantic
// Settings and stay there (not bundled into Vite's public env). The
// values below mirror the documented defaults — operators tune them
// via the agent pod's env, not the cockpit.
const REASONING_THRESHOLDS = {
  approvalAgeDays: 5,
  styleDriftCosine: 0.80,
  handoverNearDays: 14,
} as const;
import { MEMORY_SCHEMA_VERSION, MEMORY_KINDS, MEMORY_ONTOLOGY } from "../data/generated/memory-schema.generated";
import { CHAT_PROMPTS } from "../data/generated/chat-prompts.generated";
import { PROMPT_CATALOG_LINE_COUNT, PROMPT_CATALOG_BYTE_SIZE } from "../data/generated/prompt-catalog.generated";

/* Live system map. Every value here is read from env or codegen output —
   nothing hardcoded. CLAUDE.md §1. */

export default function AdminSystem(): JSX.Element {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <Card title="Runtime" icon={<Cpu className="w-4 h-4 text-foyr" />}>
        <Row k="Brand"          v={env.VITE_BRAND_NAME} />
        <Row k="Contact"        v={env.VITE_CONTACT_EMAIL} />
        <Row k="Demo seed"      v={env.VITE_DEMO_SEED} mono />
        <Row k="Public URL"     v={env.VITE_PUBLIC_URL} mono />
      </Card>

      <Card title="Reasoning thresholds" icon={<Layers className="w-4 h-4 text-foyr" />}>
        <Row k="Why · max depth"           v={String(env.VITE_WHY_MAX_DEPTH)} mono />
        <Row k="Why · top-k paths"         v={String(env.VITE_WHY_TOP_K_PATHS)} mono />
        <Row k="Memory · min accepts"      v={String(env.VITE_MEMORY_MIN_ACCEPTS)} mono />
        <Row k="Memory · max reject ratio" v={String(env.VITE_MEMORY_MAX_REJECT_RATIO)} mono />
        <Row k="Approval age threshold"    v={`${REASONING_THRESHOLDS.approvalAgeDays}d`} mono />
        <Row k="Style drift cosine"        v={String(REASONING_THRESHOLDS.styleDriftCosine)} mono />
        <Row k="Handover near"             v={`${REASONING_THRESHOLDS.handoverNearDays}d`} mono />
      </Card>

      <Card title="Prompts" icon={<Database className="w-4 h-4 text-foyr" />}>
        <Row k="Prompt cards"      v={String(CHAT_PROMPTS.length)} mono />
        <Row k="Catalog lines"     v={String(PROMPT_CATALOG_LINE_COUNT)} mono />
        <Row k="Catalog size"      v={`${(PROMPT_CATALOG_BYTE_SIZE / 1024).toFixed(1)} KB`} mono />
      </Card>

      <Card title="Memory" icon={<Boxes className="w-4 h-4 text-foyr" />}>
        <Row k="Schema version" v={MEMORY_SCHEMA_VERSION} mono />
        <Row k="Kinds"          v={String(MEMORY_KINDS.length)} mono />
        <div className="mt-1 text-[10.5px] text-ink-500">
          IRI alias: {Object.entries(MEMORY_ONTOLOGY).map(([k, v]) => `${k}=${v}`).join(" · ")}
        </div>
      </Card>

      <Card title="Protocols" icon={<Network className="w-4 h-4 text-foyr" />}>
        <Row k="A2A"            v="agent-card · tasks/send · sendSubscribe" />
        <Row k="AG-UI"          v="SSE · TEXT_MESSAGE_* · TOOL_CALL_* · STATE_*" />
        <Row k="MCP"            v="initialize · tools/list · tools/call · resources/*" />
      </Card>

      <Card title="Live health" icon={<Activity className="w-4 h-4 text-foyr" />}>
        <div className="text-[11.5px] text-ink-500">Probes will surface here once the agent pod is online.</div>
      </Card>
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: JSX.Element; children: React.ReactNode }): JSX.Element {
  return (
    <div className="panel p-3">
      <div className="flex items-center gap-1.5 mb-2">
        {icon}
        <div className="font-semibold text-ink-950 text-[13px]">{title}</div>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12px]">
      <span className="text-ink-500">{k}</span>
      <span className={mono ? "font-mono text-ink-950 tabular truncate" : "text-ink-950 truncate"}>{v}</span>
    </div>
  );
}
